import { Button, TextField, useMediaQuery } from '@mui/material'
import Grid from '@mui/material/Grid2'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useFormik } from 'formik'
import { filter, map } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import { ModalDialogWrapper } from '../../components/ModalDialogWrapper'
import { useProfileStore } from '../../store/storeProfile'
import { theme } from '../../theme'

const getValidationSchema = (t) => yup.object({
    params1: yup
        .number()
        .required(t('finances.pleaseEnterValue'))
        .typeError(t('finances.mustBeNumber'))
        .min(100, t('finances.minValue', { min: 100 }))
        .max(10000, t('finances.maxValue', { max: 10000 })),
})

interface Props {
    open: boolean
    handleClose: () => void
    type: 'success' | 'change' | 'connect'
}

const Form = ({ init, tariffListByUser, isChange, handleClose }) => {
    const { t } = useTranslation()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const { createRecurringPayment, changeRecurringPayment } = useProfileStore((state) => state)

    const { handleSubmit, values, handleBlur, touched, errors, handleChange, isValid, dirty } =
        useFormik({
            initialValues: {
                params1: null,
                params2: init,
            },
            validationSchema: getValidationSchema(t),
            onSubmit: async (values) => {
                if (isChange) {
                    await changeRecurringPayment({
                        is_active: true,
                        minimum_amount: values.params1,
                        tariff_uuid: values.params2,
                    })
                    handleClose();
                } else {
                    await createRecurringPayment({
                        is_active: true,
                        minimum_amount: values.params1,
                        tariff_uuid: values.params2,
                    })
                }
            },
        })

    return (
        <form onSubmit={handleSubmit}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
                <Stack>
                    <Typography variant="body2" mt={2}>
                        {t('finances.whenBalanceBelow')}
                    </Typography>
                    <TextField
                        required
                        sx={{
                            marginBottom: '16px',
                            marginTop: 0,
                            width: isMobile ? '100%' : '218px',
                        }}
                        margin="normal"
                        fullWidth
                        id="params1"
                        name="params1"
                        value={values.params1}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.params1 && Boolean(errors.params1)}
                        helperText={touched.params1 && errors.params1}
                    />
                </Stack>
                <Stack sx={{ marginTop: isMobile ? 0 : 2 }}>
                    <Typography variant="body2" mt={isMobile ? 0 : 2}>
                        {t('finances.replenishBalanceBy')}
                    </Typography>
                    <Select
                        MenuProps={{
                            anchorOrigin: {
                                vertical: 'top',
                                horizontal: 'center',
                            },
                            transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                            },
                        }}
                        sx={{ mr: 1, overflow: 'auto', width: isMobile ? '100%' : '218px' }}
                        id="params2"
                        name="params2"
                        value={values.params2}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="secondary">
                        {map(tariffListByUser, (value, key) => {
                            return (
                                <MenuItem
                                    value={value.id}
                                    key={key}
                                    sx={{
                                        borderBottom: '1px solid #F4F4F6',
                                        p: '10px 16px',
                                    }}>
                                    <Grid
                                        container
                                        alignItems="center"
                                        sx={{ width: '100%', m: 0 }}>
                                        <Typography variant="h6">
                                            {value.price + value.bonus_coins} {t('finances.coins')} ({value.price}{' '}
                                            ₽)
                                        </Typography>
                                    </Grid>
                                </MenuItem>
                            )
                        })}
                    </Select>
                </Stack>
            </Stack>

            <Button
                type="submit"
                disabled={!(isValid && dirty)}
                fullWidth
                variant="contained"
                sx={{ marginTop: isMobile ? 3 : 0 }}>
                {isChange ? t('finances.update') : t('finances.addPaymentMethod')}
            </Button>
        </form>
    )
}

export const PaymentSuccessfulDialog = ({ open, handleClose, type }) => {
    const { t } = useTranslation()
    const [tariffListByUser, setTariffListByUser] = useState()
    const { createRecurringPayment, tariffList, userTariff } = useProfileStore((state) => state)
    const isSuccessType = 'success' === type

    const handleClickSubmit = () => {
        handleClose()
    }

    useEffect(() => {
        if (userTariff && tariffList) {
            const data = filter(tariffList, {
                tariff_type: userTariff?.tariff?.tariff_type,
            })
            setTariffListByUser(data)
        }
    }, [tariffList, userTariff])

    const handleClickClose = () => {
        handleClose()
    }

    return (
        <ModalDialogWrapper
            open={open}
            handleSubmit={handleClickSubmit}
            handleClose={handleClickClose}
            colorSubmitButton="primary"
            title={isSuccessType ? t('finances.balanceSuccessfullyReplenished') : t('finances.autoReplenishmentSetup')}>
            {isSuccessType && (
                <Typography variant="subtitle2" sx={{ color: '#1B1C1D' }}>
                    {t('finances.autoReplenishmentSetup')}
                </Typography>
            )}
            <Typography variant="body2" mb={1}>
                {type === 'change'
                    ? t('finances.changeThresholdOrAmount')
                    : t('finances.connectAutoReplenishment')}
            </Typography>
                <Stack p={2} sx={{ background: '#EBF5FD', borderRadius: '12px' }}>
                <Typography variant="subtitle2" mb={0.5}>
                    {t('finances.howItWorks')}
                </Typography>
                {type === 'change' ? (
                    <ol style={{ margin: 0, padding: '0 20px' }}>
                        <li>
                            <Typography variant="body2">
                                {t('finances.howItWorksChangeStep1')}
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                {t('finances.howItWorksChangeStep2')}
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                {t('finances.howItWorksChangeStep3')}
                            </Typography>
                        </li>
                    </ol>
                ) : (
                    <ol style={{ margin: 0, padding: '0 20px' }}>
                        <li>
                            <Typography variant="body2">{t('finances.howItWorksStep1')}</Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                {t('finances.howItWorksStep2')}
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                {t('finances.howItWorksStep3')}
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                {t('finances.howItWorksStep4')}
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                {t('finances.howItWorksStep5')}
                            </Typography>
                        </li>
                    </ol>
                )}
            </Stack>

            {tariffListByUser && (
                <Form
                    handleClose={handleClose}
                    init={tariffListByUser[0]?.id}
                    tariffListByUser={tariffListByUser}
                    isChange={type === 'change'}
                />
            )}
        </ModalDialogWrapper>
    )
}
